import { Component, LLMProvider, Tool, DashCompModelConfig, Message, T_RESPONSE } from "../types";
import { DEFAULT_DASH_COMP_MODELS } from "./types";

/**
 * Get API key and model based on provider configuration
 */
export function getApiKeyAndModel(
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	dashCompModels?: DashCompModelConfig
): { apiKey: string | undefined; model: string | undefined } {
	const providers = llmProviders || ['anthropic', 'gemini', 'openai', 'groq'];
	let apiKey: string | undefined;
	let model: string | undefined;

	// Check if custom model is configured via dashCompModels
	if (dashCompModels?.model) {
		model = dashCompModels.model;
		const modelProvider = model.split('/')[0] as LLMProvider;

		if (modelProvider === 'anthropic') apiKey = anthropicApiKey;
		else if (modelProvider === 'gemini') apiKey = geminiApiKey;
		else if (modelProvider === 'openai') apiKey = openaiApiKey;
		else if (modelProvider === 'groq') apiKey = groqApiKey;
	} else {
		for (const provider of providers) {
			if (provider === 'anthropic' && anthropicApiKey) {
				apiKey = anthropicApiKey;
				model = DEFAULT_DASH_COMP_MODELS.anthropic;
				break;
			} else if (provider === 'gemini' && geminiApiKey) {
				apiKey = geminiApiKey;
				model = DEFAULT_DASH_COMP_MODELS.gemini;
				break;
			} else if (provider === 'openai' && openaiApiKey) {
				apiKey = openaiApiKey;
				model = DEFAULT_DASH_COMP_MODELS.openai;
				break;
			} else if (provider === 'groq' && groqApiKey) {
				apiKey = groqApiKey;
				model = DEFAULT_DASH_COMP_MODELS.groq;
				break;
			}
		}
	}

	// OpenRouter fail-open — mirror the script/agent flow. When no first-party
	// provider key resolved but an OpenRouter key is configured, route through
	// OpenRouter instead of bailing out. LLM._parseModel rewrites an anthropic/*
	// model to the OpenRouter slug (ANTHROPIC_API_KEY absent + OPENROUTER_API_KEY
	// present), so selecting the Anthropic default here makes the reroute fire.
	if (!apiKey && process.env.OPENROUTER_API_KEY) {
		apiKey = process.env.OPENROUTER_API_KEY;
		if (!model || !model.startsWith('anthropic/')) {
			model = DEFAULT_DASH_COMP_MODELS.anthropic;
		}
	}

	return { apiKey, model };
}

/**
 * Format components for prompt
 */
export function formatComponentsForPrompt(components: Component[]): string {
	if (!components || components.length === 0) {
		return 'No components available';
	}
	components = components.filter(c => c.name !== 'MultiComponentContainer');
	return components
		.map((comp, idx) => {
			const keywords = comp.keywords ? comp.keywords.join(', ') : '';
			const propsPreview = comp.props ? JSON.stringify(comp.props, null, 2) : 'No props';
			return `${idx + 1}. ID: ${comp.id}
   Name: ${comp.name}
   Type: ${comp.type}
   Description: ${comp.description || 'No description'}
   Keywords: ${keywords}
   Props Structure: ${propsPreview}`;
		})
		.join('\n\n');
}

/**
 * Classify a tool so the prompt can render it under the right heading and the
 * LLM can apply the priority rule (direct tools beat SQL when both can answer).
 * `toolType: 'direct'` is the authoritative signal; the toolId suffix is a
 * fallback for source tools that don't set toolType.
 */
function classifyTool(tool: Tool): 'direct' | 'sql' | 'rest' | 'graphql' {
	if (tool.toolType === 'direct') return 'direct';
	if (tool.id.endsWith('_query')) return 'sql';
	if (tool.id.endsWith('_call')) return 'rest';
	if (tool.id.endsWith('_graphql')) return 'graphql';
	return 'direct';
}

/**
 * Format tools for prompt. Direct tools are listed first under their own
 * heading so the LLM applies them by default; SQL/REST/GraphQL source tools
 * follow as the fallback for questions no direct tool can answer.
 */
export function formatToolsForPrompt(tools?: Tool[]): string {
	if (!tools || tools.length === 0) {
		return 'No external tools available.';
	}
	const directTools: Tool[] = [];
	const sourceTools: Tool[] = [];
	for (const t of tools) {
		(classifyTool(t) === 'direct' ? directTools : sourceTools).push(t);
	}

	const renderTool = (tool: Tool, idx: number, label: string) => {
		const paramsStr = Object.entries(tool.params || {})
			.map(([key, type]) => `${key}: ${type}`)
			.join(', ');
		return `${idx + 1}. [${label}] ID: ${tool.id}
   Name: ${tool.name}
   Description: ${tool.description}
   Parameters: { ${paramsStr} }`;
	};

	const sections: string[] = [];
	if (directTools.length > 0) {
		const body = directTools.map((t, i) => renderTool(t, i, 'DIRECT — PREFER')).join('\n\n');
		sections.push(`### Direct Tools (try these first — they answer most questions without SQL)\n${body}`);
	}
	if (sourceTools.length > 0) {
		const body = sourceTools
			.map((t, i) => {
				const kind = classifyTool(t);
				const label = kind === 'sql' ? 'SQL' : kind === 'rest' ? 'REST' : 'GRAPHQL';
				return renderTool(t, i, label);
			})
			.join('\n\n');
		sections.push(`### Source Tools (fallback — use only when no direct tool fits)\n${body}`);
	}
	return sections.join('\n\n');
}

/**
 * Format existing components for filter prompt
 */
export function formatExistingComponentsForPrompt(existingComponents?: Component[]): string {
	if (!existingComponents || existingComponents.length === 0) {
		return 'No existing components in dashboard';
	}
	return JSON.stringify(existingComponents, null, 2);
}

/**
 * Send DASH_COMP_RES response message
 */
export function sendDashCompResponse(
	id: string,
	res: T_RESPONSE,
	sendMessage: (message: Message) => void,
	clientId?: string
): void {
	const response: Message = {
		id,
		type: 'DASH_COMP_RES',
		from: { type: 'data-agent' },
		to: {
			type: 'runtime',
			id: clientId
		},
		payload: {
			...res
		}
	};

	sendMessage(response);
}
